import { forwardRef, useState, useImperativeHandle, useEffect } from 'react';
import { colorToArray, displayPromoHeader, ensureBoolean, formatNumber, getLayout, getLayoutStage, isFooterOn, isNotEmpty, isColorBright, RGB_2_HEX, getLayoutQuiz } from '../../lib/ep';

const CssVars = forwardRef((props, ref) => {
	const is_builder = props?.is_builder ?? false;
	const in_template = props?.in_template ?? false;
	const ep_blue = 'rgba(30, 34, 170, 1)';

	const selector = props?.css_selector ?? ((is_builder) ? '.css_var_root' : ':root');	//.builder_canvas_container
	const main_font = (is_builder) ? '"SanFrancisco", "BlinkMacSystemFont", Helvetica, Arial, sans-serif' : 'Helvetica, Arial, sans-serif';

	let display_header = displayPromoHeader(props.promotion);
	let hide_footer = !isFooterOn(props.promotion);

	// In builder we disable this so the changes on enabling / disabling the promotion header and / or footer
	// doesn't have implications such as having a header height of 0px after re-enabling it (without reloading).
	// To-do: detect somehow the change of header or footer enabled, we can remove this if and let
	// the builder have the same behaviour as in production.
	if(is_builder) {
		display_header = true;
		hide_footer = false;
	}

	const promo_layout_props = getLayout(props.promotion);

	if(props.stage != undefined) {
		promo_layout_props.stage = getLayoutStage(props.stage, promo_layout_props);
	}

	if(props.quiz != undefined) {
		promo_layout_props.quiz = getLayoutQuiz(props.quiz, promo_layout_props);
	}

	const [ footer_h, setFooterH ] = useState((!hide_footer) ? '50px' : '0px');
	const [ layout_props, setLayoutProps ] = useState(promo_layout_props);

	const header_type = layout_props.header?.type ?? 'classic';

	let menu_position = layout_props.header?.menu_position ?? 'row';
	if(header_type == 'tab') menu_position = 'row';

	useEffect(() => {
		if(hide_footer || in_template) return;

		setTimeout(() => {
			const footer_element = document.querySelector('.gui_footer');
			setFooterH((footer_element != undefined) ? `${footer_element.offsetHeight}px` : '0px');
		}, 0);
	}, []);

	useImperativeHandle(ref, () => ({
		setCustomStyles(s) {
			setLayoutProps(s);
		},
	}), []);

	const getHPosition = (anchor) => {
		switch(anchor) {
			case 'tl':
			case 'cl':
			case 'bl':
			case 'start':
				return 'flex-start';

			case 'tc':
			case 'cc':
			case 'bc':
			case 'center':
				return 'center';

			case 'tr':
			case 'cr':
			case 'br':
			case 'end':
				return 'flex-end';
		}

		return 'center';
	};

	const getVPosition = (anchor) => {
		switch(anchor) {
			case 'tl':
			case 'tc':
			case 'tr':
				return 'flex-start';

			case 'cl':
			case 'cc':
			case 'cr':
				return 'center';

			case 'bl':
			case 'bc':
			case 'br':
				return 'flex-end';
		}

		return 'center';
	};

	const getVHPosition = (anchor) => {
		// enable compatibility with explicit pre-processed values (for old promos):
		if(anchor.length > 2) return anchor;

		let v_comp;
		let h_comp;

		const anchor_parts = anchor.split("");

		if(anchor_parts[0] == 't') v_comp = 'top';
		if(anchor_parts[0] == 'c') v_comp = 'center';
		if(anchor_parts[0] == 'b') v_comp = 'bottom';

		if(anchor_parts[1] == 'l') h_comp = 'left';
		if(anchor_parts[1] == 'c') h_comp = 'center';
		if(anchor_parts[1] == 'r') h_comp = 'right';

		return `${v_comp} ${h_comp}`;
	};

	const getBlockMargin = (direction, text_align) => {
		switch(text_align) {
			case 'start':
				return (direction == 'start') ? '0' : 'auto';

			case 'end':
				return (direction == 'start') ? 'auto' : '0';
				
			case 'center':
			case 'justify':
				return 'auto';
		}
	}

	const getBackgroundSize = (v) => {
		switch(v) {
			case 'cover':
			case 'contain':
				return v;

			case '1':
			case '5':
				// enable compatibility with explicit pre-processed values (for old promos):
				return 'cover';
		}

		return 'cover';
	};

	const getHeaderHeight = () => {
		if(!display_header) return '0px';
		
		if(header_type == 'simple') return '60px';
		if(layout_props.header?.tall_header ?? false) return '60px';
		return '36px';
	};

	const apply_ink = ensureBoolean(layout_props.background?.apply_ink ?? 'false');

	const is_text_color_light = (isColorBright(RGB_2_HEX(`rgba(${colorToArray(layout_props.color.text_color)}, 1)`))) ? true : false;
	const override_question_btn_styles = (layout_props?.quiz?.continue ?? false) ? ensureBoolean(layout_props.quiz.continue?.override_styles ?? false) : false;

	const css_vars = `${selector} {
		--forced-text-color:${colorToArray('#000000')};
		--forced-bg-color:${colorToArray('#FFFFFF')};
		--ep-color:${colorToArray(ep_blue)};

		/* COLOR - ini */
		--main-color-sec:${colorToArray(layout_props.color.main_color)};
		--main-color:var(--main-color-sec);
		--main-color-text:${(isColorBright(RGB_2_HEX(`rgba(${colorToArray(layout_props.color.main_color)}, 1)`))) ? '0,0,0,1' : '255,255,255,1'};
		--secondary-color-sec:${colorToArray(layout_props.color.text_color)};
		--secondary-color:var(--secondary-color-sec);
		--text-color-sec:${colorToArray(layout_props.color.text_color, true)};
		--text-color:var(--text-color-sec);
		--text-color-rgb:${colorToArray(layout_props.color.text_color)};
		--text-color-hi-contrast:${(is_text_color_light) ? '255,255,255' : '0,0,0'};
		--background-color-hi-contrast:${(is_text_color_light) ? '0,0,0' : '255,255,255'};
		--outline-color:var(--main-color);
		--container-background-color-rgb:${colorToArray(layout_props.color.container_color)};
		--container-background-color-sec:${colorToArray(layout_props.color.container_color, true)};
		--container-background-color:var(--container-background-color-sec);
		--error-color:${colorToArray(layout_props.color.error_color)};
		/* COLOR - end */

		/* TEXT - ini */
		--font-size-multiplier-sec:${parseFloat(layout_props.text.font_size_multiplier)};
		--font-size-multiplier:var(--font-size-multiplier-sec);
		--main-font-sec:${layout_props.text.main_font_css ?? main_font};
		--main-font:var(--main-font-sec);
		--main-font-light:${(layout_props.text.main_font_type == 'google') ? layout_props.text.main_font_light : '100'};
		--main-font-roman:${(layout_props.text.main_font_type == 'google') ? layout_props.text.main_font_roman : '400'};
		--main-font-bold:${(layout_props.text.main_font_type == 'google') ? layout_props.text.main_font_bold : '700'};

		--light-font:var(--main-font-light);
		--lighter-font:var(--main-font-light);
		--roman-font:var(--main-font-roman);
		--bold-font:var(--main-font-bold);
		--xbold-font:var(--main-font-bold);

		--secondary-font-sec:${layout_props.text.secondary_font_css};
		--secondary-font:var(--secondary-font-sec);
		--secondary-font-light:${(layout_props.text.secondary_font_type == 'google') ? layout_props.text.secondary_font_light : '100'};
		--secondary-font-roman:${(layout_props.text.secondary_font_type == 'google') ? layout_props.text.secondary_font_roman : '400'};
		--secondary-font-bold:${(layout_props.text.secondary_font_type == 'google') ? layout_props.text.secondary_font_bold : '700'};

		--default-font-size:0.93rem;
		--default-line-height:1.3em;
		--text-block-max-width:100%;
		--text-block-font-size:10.5pt;

		--text-default-align:center;
		--text-text-align:${layout_props.text.text_align};
		--text-text-align-flex:${getHPosition(layout_props.text.text_align)};
		--text-default-align-flex:${layout_props.text.text_align_flex};
		--text-block-width:${parseInt(layout_props.text.block_width)}%;
		--text-block-margin-start:${getBlockMargin('start', layout_props.text.text_align)};
		--text-block-margin-end:${getBlockMargin('end', layout_props.text.text_align)};

		--h1-color:${colorToArray(layout_props.text.h1_color, true)};
		--h1-font-size:${layout_props.text.h1_font_size}rem;
		--h1-line-height:${layout_props.text.h1_line_height}em;
		--h1-font-family:var(--${layout_props.text.h1_font_family}-font);
		--h1-font-weight:var(--${layout_props.text.h1_font_family}-font-${layout_props.text.h1_font_weight});
		--h1-text-transform:${(ensureBoolean(layout_props.text?.h1_force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		--h1-text-align:${layout_props.text.h1_text_align};

		--h2-color:${colorToArray(layout_props.text.h2_color, true)};
		--h2-font-size:${layout_props.text.h2_font_size}rem;
		--h2-line-height:${layout_props.text.h2_line_height}em;
		--h2-font-family:var(--${layout_props.text.h2_font_family}-font);
		--h2-font-weight:var(--${layout_props.text.h2_font_family}-font-${layout_props.text.h2_font_weight});
		--h2-text-transform:${(ensureBoolean(layout_props.text?.h2_force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		--h2-text-align:${layout_props.text.h2_text_align};

		--h3-color:${colorToArray(layout_props.text.h3_color, true)};
		--h3-font-size:${layout_props.text.h3_font_size}rem;
		--h3-line-height:${layout_props.text.h3_line_height}em;
		--h3-font-family:var(--${layout_props.text.h3_font_family}-font);
		--h3-font-weight:var(--${layout_props.text.h3_font_family}-font-${layout_props.text.h3_font_weight});
		--h3-text-transform:${(ensureBoolean(layout_props.text?.h3_force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		--h3-text-align:${layout_props.text.h3_text_align};

		--p-color:${colorToArray(layout_props.text.p_color, true)};
		--p-font-size:${layout_props.text.p_font_size}rem;
		--p-line-height:${layout_props.text.p_line_height}em;
		--p-font-family:var(--${layout_props.text.p_font_family}-font);
		--p-font-weight:var(--${layout_props.text.p_font_family}-font-${layout_props.text.p_font_weight});
		--p-text-align:${layout_props.text.p_text_align};

		--text-ul-list-style:${(layout_props.text.ul_list_style != 'custom') ? layout_props.text.ul_list_style : `"${layout_props.text.ul_list_style_custom}"`};
		--text-ul-padding-start:${(layout_props.text.ul_list_style != 'none') ? layout_props.text.ul_padding_start : '0'}em;
		--text-ul-padding-end:${layout_props.text.ul_padding_end}em;
		--text-ul-margin-bottom:${layout_props.text.ul_margin_bottom}em;
		--text-ol-list-style:decimal;
		--text-ol-padding-start:${(layout_props.text.ul_list_style != 'none') ? layout_props.text.ul_padding_start : '1'}em;
		--text-ol-padding-end:${layout_props.text.ul_padding_end}em;

		--stage-section-title-text-align:${layout_props.text.sections_text_align};
		/* TEXT - end */

		/* BUTTON - ini */
		--button-background-color:${colorToArray(layout_props.button.background_color, true)};
		--button-text-color:${colorToArray(layout_props.button.color, true)};
		--button-text-color-rgb:${colorToArray(layout_props.button.color)};
		--button-size:${layout_props.button.size};
		--button-border-radius:${formatNumber(layout_props.button.border_radius, 1)}em;
		--button-border-width:${parseInt(layout_props.button.border_width)}px;
		--button-border-color:${colorToArray(layout_props.button.border_color, true)};
		--button-background-color-hover:${colorToArray(layout_props.button.background_color_hover, true)};
		--button-text-color-hover:${colorToArray(layout_props.button.color_hover, true)};
		--button-text-color-rgb-hover:${colorToArray(layout_props.button.color_hover)};
		--button-border-color-hover:${colorToArray(layout_props.button.border_color_hover, true)};
		--button-background-color-disabled:${colorToArray(layout_props.button.background_color_disabled, true)};
		--button-text-color-disabled:${colorToArray(layout_props.button.color_disabled, true)};
		--button-text-color-rgb-disabled:${colorToArray(layout_props.button.color_disabled)};
		--button-border-color-disabled:${colorToArray(layout_props.button.border_color_disabled, true)};
		--button-text-transform:${(ensureBoolean(layout_props.button?.force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		--button-font-family:var(--${layout_props.button.font_family}-font);
		--button-font-weight:var(--${layout_props.button.font_family}-font-${layout_props.button.font_weight});
		--button-background-image:${(isNotEmpty(layout_props.button, 'full_button_bg_image')) ? `url(${layout_props.button.full_button_bg_image})` : 'none'};
		--button-opacity:1;
		--button-opacity-hover:${(isNotEmpty(layout_props.button, 'full_button_bg_image')) ? '0.8' : 'var(--button-opacity)'};
		/* BUTTON - end */

		/* LINK - ini */
		--link-color-sec:${colorToArray(layout_props.link.color, true)};
		--link-color-rgb:${colorToArray(layout_props.link.color)};
		--link-color:var(--link-color-sec);
		--link-decoration:${layout_props.link.decoration};
		--link-color-hover:${colorToArray(layout_props.link.color_hover, true)};
		--link-decoration-hover:${layout_props.link.decoration_hover};
		--link-text-transform:${(ensureBoolean(layout_props.link?.force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		--link-font-family:var(--${layout_props.link.font_family}-font);
		--link-font-weight:var(--${layout_props.link.font_family}-font-${layout_props.link.font_weight});
		/* LINK - end */

		/* FIELD - ini */
		--field-font-family:var(--${layout_props.field.font_family}-font);
		--field-font-weight:var(--${layout_props.field.font_family}-font-${layout_props.field.font_weight});
		--field-font-size:${layout_props.field.font_size}rem;
		--field-border-color:${colorToArray(layout_props.field.border_color, true)};
		--field-border-width:${parseInt(layout_props.field.border_width)}px;
		${(layout_props.field.border_type != 'all') ? `--field-border-top:none; --field-border-bottom:none; --field-border-left:none; --field-border-right:none; --field-border-${layout_props.field.border_type}:var(--field-border-width) solid rgba(var(--field-border-color));` : `--field-border-top:var(--field-border-width) solid rgba(var(--field-border-color)); --field-border-bottom:var(--field-border-width) solid rgba(var(--field-border-color)); --field-border-left:var(--field-border-width) solid rgba(var(--field-border-color));--field-border-right:var(--field-border-width) solid rgba(var(--field-border-color));`}
		--field-background-color-rgb:${colorToArray(layout_props.field.background_color)};
		--field-background-color:${colorToArray(layout_props.field.background_color, true)};
		--field-color:${colorToArray(layout_props.field.color, true)};
		--field-color-rgb:${colorToArray(layout_props.field.color)};
		--field-border-radius:${formatNumber(layout_props.field.border_radius, 1)}em;
		/* FIELD - end */

		/* FIELD LABEL - ini */
		--field-label-font-family:var(--${layout_props.field_label.font_family}-font);
		--field-label-font-weight:var(--${layout_props.field_label.font_family}-font-${layout_props.field_label.font_weight});
		--field-label-font-size:${layout_props.field_label.font_size}rem;
		--field-label-color-rgb:${colorToArray(layout_props.field_label.color)};
		--field-label-color:${colorToArray(layout_props.field_label.color, true)};
		--field-label-text-transform:${(ensureBoolean(layout_props.field_label?.force_uppercase ?? 'true')) ? 'uppercase' : 'none'};
		/* FIELD LABEL - end */

		/* LAYER - ini */
		--layer-overlay-color:${colorToArray(layout_props.layer.overlay_color, true)};
		--layer-background-color:${colorToArray(layout_props.layer.background_color)};
		--layer-text-color-rgb:${colorToArray(layout_props.layer.color)};
		--layer-text-color:${colorToArray(layout_props.layer.color, true)};
		--layer-main-color:${colorToArray(layout_props.layer.link_color)};
		--layer-field-border-width:${Math.max(1, parseInt(layout_props.field.border_width))}px;
		${(layout_props.field.border_type != 'all') ? `--layer-field-border-${layout_props.field.border_type}:var(--layer-field-border-width) solid rgba(var(--field-border-color));` : `--layer-field-border-top:var(--layer-field-border-width) solid rgba(var(--layer-text-color)); --layer-field-border-bottom:var(--layer-field-border-width) solid rgba(var(--layer-text-color)); --layer-field-border-left:var(--layer-field-border-width) solid rgba(var(--layer-text-color));--layer-field-border-right:var(--layer-field-border-width) solid rgba(var(--layer-text-color));`}
		--layer-header-h:2rem;
		--layer-header-background-color:${colorToArray(layout_props.layer.header_background_color, true)};
		--layer-header-color:${colorToArray(layout_props.layer.header_color)};
		--layer-header-border-color:${colorToArray(layout_props.layer.header_background_color)}, 0.05;
		--layer-border-radius:${formatNumber(layout_props.layer.border_radius, 1)}rem;
		/* LAYER - end */

		/* BACKGROUND - ini */
		--background-position:${getVHPosition(layout_props.background.anchor)};
		--background-size:${getBackgroundSize(layout_props.background.repeat)};
		--background-color:${colorToArray(layout_props.color.background_color, true)};
		--background-ink-color:${(apply_ink) ? `${colorToArray(layout_props.background.ink_color, true)}` : '0, 0, 0, 0'};
		--background-blend-mode:${(apply_ink) ? `${layout_props.background.blend_mode}` : 'normal'};
		/* BACKGROUND - end */

		/* HEADER - ini */
		--header-position:${(ensureBoolean(layout_props.header.sticky ?? 'true')) ? 'sticky' : 'relative'};
		--header-background-color:${colorToArray(layout_props.header.background_color, true)};
		--header-background-color-rgb:${colorToArray(layout_props.header.background_color)};
		--header-menu-background-color:${colorToArray(layout_props.header.menu_background_color, true)};
		--header-menu-background-color-rgb:${colorToArray(layout_props.header.menu_background_color)};
		--header-menu-background-color-hover:${colorToArray(layout_props.header.menu_background_color_hover, true)};
		--header-menu-background-color-hover-rgb:${colorToArray(layout_props.header.menu_background_color_hover)};
		--header-border-bottom-width:${(display_header && parseInt(layout_props.header?.border_width ?? '1') > 0) ? `${layout_props.header.border_width}px` : '0px'};
		--header-border-bottom:${(parseInt(layout_props.header?.border_width ?? '1') > 0) ? `${layout_props.header.border_width}px solid rgba(${colorToArray(layout_props.header.border_color, true)})` : 'none'};
		--header-display-icons:${(ensureBoolean(layout_props.header?.display_icons ?? 'true')) ? 'initial' : 'none'};
		--header-menu-position:${menu_position};
		--header-logo-position:${(menu_position == 'row') ? 'start' : 'end'};
		--header-submenu-right:${(menu_position == 'row') ? '0.5em' : 'unset'};
		--header-submenu-left:${(menu_position == 'row-reverse') ? '0.5em' : 'unset'};
		--header-menu-color:${colorToArray(layout_props.header.menu_color, true)};
		--header-menu-border:${(parseInt(layout_props.header?.menu_border_width ?? '1') > 0) ? `${layout_props.header?.menu_border_width}px ${layout_props.header?.menu_border_style} rgba(${colorToArray(layout_props.header.menu_border_color, true)})` : 'none'};
		--header-menu-color-rgb:${colorToArray(layout_props.header.menu_color)};
		--header-menu-color-hover:${colorToArray(layout_props.header.menu_color_hover, true)};
		--header-menu-letter-spacing:0px;
		--header-link-color-rgb:${colorToArray(layout_props.header.link_color)};
		--header-link-color:${colorToArray(layout_props.header.link_color, true)};
		--header-link-color-hover:${colorToArray(layout_props.header.link_color_hover, true)};
		--header-link-background-color-hover:${colorToArray(layout_props.header.link_background_color_hover, true)};
		--header-menu-cover-background-color:${colorToArray(layout_props.header.menu_cover_background_color)}, 0.75;
		--header-opened-color-rgb:${(isColorBright(RGB_2_HEX(layout_props.header.menu_background_color))) ? '0, 0, 0' : '255, 255, 255'};
		--header-h:${getHeaderHeight()};
		--header-h-on-scroll:${(ensureBoolean(layout_props.header.sticky ?? 'true')) ? 'var(--header-h)' : 'calc(0.25 * var(--header-h))'};
		--header-gallery-cart-position:fixed;
		--header-gallery-cart-top:calc(var(--header-h) + var(--test-msg-h));
		/* HEADER - end */

		/* FOOTER - ini */
		--footer-background-color:${colorToArray(layout_props.footer.background_color, true)};
		--footer-border-top-width:${(!hide_footer && parseInt(layout_props.footer?.border_width ?? '1') > 0) ? `${layout_props.footer.border_width}px` : '0px'};
		--footer-border-top:${(parseInt(layout_props.footer?.border_width ?? '1') > 0) ? `${layout_props.footer?.border_width}px solid rgba(${colorToArray(layout_props.footer.border_color, true)})` : 'none'};
		--footer-link-color:${colorToArray(layout_props.footer.link_color, true)};
		--footer-link-color-hover:${colorToArray(layout_props.footer.link_color_hover, true)};
		--min-footer-h:${footer_h};
		/* FOOTER - end */

		/* CONTAINER - ini */
		--container-padding:${layout_props.container.padding}em;
		--container-margin:${layout_props.container.margin}em;
		--container-border-radius:${formatNumber(layout_props.container.border_radius, 1)}em;
		--container-border-width:${parseInt(layout_props.container.border_width)}px;
		--container-border-color:${colorToArray(layout_props.container.border_color, true)};
		--container-anchor:${layout_props.container.anchor};
		--container-min-height:${(layout_props.container.height == 'full') ? 'calc(100dvh - var(--header-border-bottom-width) - var(--header-h) - var(--min-footer-h) - 2 * var(--container-margin))' : '50px'};
		--container-min-height-rel:${(layout_props.container.height == 'full') ? 'calc(100% - 2 * var(--container-margin))' : '50px'};
		--container-shadow:${(ensureBoolean(layout_props.container.shadow) !== false) ? 'var(--container-shadow-x) var(--container-shadow-y) var(--container-shadow-blur) var(--container-shadow-color)' : 'none'};
		--container-h-position:${getHPosition(layout_props.container.anchor)};
		--container-v-position:${getVPosition(layout_props.container.anchor)};
		/* CONTAINER - end */

		/* LOYALTY - ini */
		--loyalty-text-color:${colorToArray(layout_props.loyalty.text_color, true)};
		--loyalty-background-color:${colorToArray(layout_props.loyalty.background_color, true)};
		--loyalty-border-width:${parseInt(layout_props.loyalty.border_width)}px;
		--loyalty-border-color:${colorToArray(layout_props.loyalty.text_color, true)};
		--loyalty-link-color:${colorToArray(layout_props.loyalty.link_color, true)};
		/* LOYALTY - end */

		/* QUIZ - ini */
		${(layout_props?.quiz?.question ?? false) ? `--quiz-text-align:${(ensureBoolean(layout_props.quiz.question?.force_align_center ?? 'false')) ? 'center' : 'start'};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-text-font-weight:var(--main-font-${layout_props.quiz.answer?.font_weight ?? 'roman'});` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-tick-color:${colorToArray(layout_props.quiz.answer.tick_color, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-tick-bg-color:${colorToArray(layout_props.quiz.answer.tick_bg_color, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-text-transform:${(ensureBoolean(layout_props.quiz.answer?.force_uppercase ?? 'false')) ? 'uppercase' : 'none'};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-border-width:${layout_props.quiz.answer.border_width}px;` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-border-color:${colorToArray(layout_props.quiz.answer.border_color, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-color:${colorToArray(layout_props.quiz.answer.color, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-bg-color:${colorToArray(layout_props.quiz.answer.background_color, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-color-hover:${colorToArray(layout_props.quiz.answer.color_hover, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-bg-color-hover:${colorToArray(layout_props.quiz.answer.background_color_hover, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-border-color-hover:${colorToArray(layout_props.quiz.answer.border_color_hover, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-color-selected:${colorToArray(layout_props.quiz.answer.color_selected, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-bg-color-selected:${colorToArray(layout_props.quiz.answer.background_color_selected, true)};` : ''}
		${(layout_props?.quiz?.answer ?? false) ? `--quiz-answer-border-color-selected:${colorToArray(layout_props.quiz.answer.border_color_selected, true)};` : ''}
		${(layout_props?.quiz?.continue ?? false) ? `--quiz-continue-button-color:${(override_question_btn_styles) ? colorToArray(layout_props.quiz.continue.color, true) : 'var(--button-text-color)'};` : ''}
		${(layout_props?.quiz?.continue ?? false) ? `--quiz-continue-button-bg-color:${(override_question_btn_styles) ? colorToArray(layout_props.quiz.continue.background_color, true) : 'var(--button-background-color)'};` : ''}
		${(layout_props?.quiz?.continue ?? false) ? `--quiz-continue-button-color-hover:${(override_question_btn_styles) ? colorToArray(layout_props.quiz.continue.color_hover, true) : 'var(--button-text-color-hover)'};` : ''}
		${(layout_props?.quiz?.continue ?? false) ? `--quiz-continue-button-bg-color-hover:${(override_question_btn_styles) ? colorToArray(layout_props.quiz.continue.background_color_hover, true) : 'var(--button-background-color-hover)'};` : ''}
		/* QUIZ - end */

		/* STAGE - ini */
		${(layout_props?.stage?.color ?? false) ? `--stage-main-color:${colorToArray(layout_props.stage.color.main_color, true)};` : ''}
		${(layout_props?.stage?.color ?? false) ? `--stage-text-color:${colorToArray(layout_props.stage.color.text_color, true)};` : ''}
		${(layout_props?.stage?.color ?? false) ? `--stage-main-color-variant:${colorToArray(layout_props.stage.color.main_color_variant, true)};` : ''}
		${(layout_props?.stage?.color ?? false) ? `--stage-bar-color:${colorToArray(layout_props.stage.color.bar_color, true)};` : ''}
		${(layout_props?.stage?.button ?? false) ? `--stage-button-background-color:${colorToArray(layout_props.stage.button.background_color, true)};` : ''}
		${(layout_props?.stage?.button ?? false) ? `--stage-button-text-color:${colorToArray(layout_props.stage.button.color, true)};` : ''}
		${(layout_props?.stage?.button ?? false) ? `--stage-button-background-color-hover:${colorToArray(layout_props.stage.button.background_color_hover, true)};` : ''}
		${(layout_props?.stage?.button ?? false) ? `--stage-button-text-color-hover:${colorToArray(layout_props.stage.button.color_hover, true)};` : ''}
		${(layout_props?.stage?.text ?? false) ? `--stage-question-text-color:${colorToArray(layout_props.stage.text.text_color, true)};` : ''}
		${(layout_props?.stage?.text ?? false) ? `--stage-question-font-family:var(--${layout_props.stage.text.font_family}-font);` : ''}
		${(layout_props?.stage?.text ?? false) ? `--stage-question-font-weight:var(--${layout_props.stage.text.font_family}-font-${layout_props.stage.text.font_weight});` : ''}
		${(layout_props?.stage?.text ?? false) ? `--stage-question-font-size:${layout_props.stage.text.font_size};` : ''}
		${(layout_props?.stage?.text ?? false) ? `--stage-question-line-height:${layout_props.stage.text.line_height}em;` : ''}
		/* STAGE - end */

		/* OTHER - ini */
		--selection-bg-color:${colorToArray(layout_props.other.selection_bg_color)};
		--selection-color:${colorToArray(layout_props.other.selection_color)};

		--ribbon-open-bg-color:${colorToArray(layout_props.other.ribbon_open_background_color, true)};
		--ribbon-open-bg-color-rgb:${colorToArray(layout_props.other.ribbon_open_background_color)};
		--ribbon-open-text-color:${colorToArray(layout_props.other.ribbon_open_text_color, true)};
		--ribbon-open-text-color-rgb:${colorToArray(layout_props.other.ribbon_open_text_color)};
		--ribbon-open-border-color:${colorToArray(layout_props.other.ribbon_open_border_color, true)};

		--ribbon-pending-bg-color:${colorToArray(layout_props.other.ribbon_pending_background_color, true)};
		--ribbon-pending-bg-color-rgb:${colorToArray(layout_props.other.ribbon_pending_background_color)};
		--ribbon-pending-text-color:${colorToArray(layout_props.other.ribbon_pending_text_color, true)};
		--ribbon-pending-text-color-rgb:${colorToArray(layout_props.other.ribbon_pending_text_color)};
		--ribbon-pending-border-color:${colorToArray(layout_props.other.ribbon_pending_border_color, true)};

		--ribbon-closed-bg-color:${colorToArray(layout_props.other.ribbon_closed_background_color, true)};
		--ribbon-closed-bg-color-rgb:${colorToArray(layout_props.other.ribbon_closed_background_color)};
		--ribbon-closed-text-color:${colorToArray(layout_props.other.ribbon_closed_text_color, true)};
		--ribbon-closed-text-color-rgb:${colorToArray(layout_props.other.ribbon_closed_text_color)};
		--ribbon-closed-border-color:${colorToArray(layout_props.other.ribbon_closed_border_color, true)};
	/* OTHER - end */
	}`;

	return css_vars;
});

export default CssVars;